import { Box, Typography } from '@ds/coolshop';
import { FormElementProps, FormValues } from '@src/assets/types';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

export const FormElement = ({
    defaultValues,
    submitHandler,
    changeHandler,
    formKey,
    formId,
    loading,
    title,
    children,
    isEditNode,
    disableSubmitOnEnter,
    disabled,
}: FormElementProps) => {
    // `disabled` is react-hook-form's own: it reaches every field through the control, so a
    // read-only form needs nothing threaded down to the components that draw the fields.
    const methods = useForm<FormValues>({
        defaultValues,
        mode: 'onChange',
        reValidateMode: 'onChange',
        disabled,
    });

    const { formState, watch, handleSubmit, getValues, reset } = methods;
    const values = getValues();
    const watchAllFields = watch();
    const { isDirty, dirtyFields } = formState;

    // Track if we just submitted to prevent changeHandler from running immediately after
    const justSubmittedRef = useRef(false);

    const formRef = useRef<HTMLFormElement>(null);

    // const ControlledFields = useMemo<React.ReactNode>(
    //     () => children({ ...methods, values, ...formState }),
    //     [children, values, isDirty],
    // );

    const ControlledFields = children({ ...methods, values, ...formState });

    async function onSubmit(data: FormValues) {
        if (submitHandler) {
            justSubmittedRef.current = true;
            const result = await submitHandler(data, dirtyFields, isDirty);
            // A submitHandler that returns false signals a failed save: keep the
            // dirty state so a retry re-sends the same fields instead of silently
            // dropping the changes that failed to persist.
            if (result === false) {
                justSubmittedRef.current = false;
                return;
            }
            // Reset form after successful submit to clear dirtyFields
            // This sets form values to submitted data and clears the dirty state
            // The useEffect will detect empty dirtyFields and call changeHandler(false)
            reset(data);
        }
    }

    useEffect(() => {
        if (changeHandler && !justSubmittedRef.current) {
            changeHandler(!!Object.keys(dirtyFields).length);
        } else if (
            changeHandler &&
            justSubmittedRef.current &&
            Object.keys(dirtyFields).length === 0
        ) {
            // If we just submitted and dirtyFields is now empty, ensure isDirty is set to false
            changeHandler(false);
            justSubmittedRef.current = false;
        }
    }, [watchAllFields, dirtyFields, changeHandler]);

    useEffect(() => {
        if (formKey && !loading) {
            reset(defaultValues);
        }
    }, [formKey, loading]);

    useEffect(() => {
        if (!disableSubmitOnEnter) return undefined;
        const formEl = formRef.current;
        if (!formEl) return undefined;
        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            const tag = target.tagName;
            if (
                e.key === 'Enter' &&
                tag !== 'TEXTAREA' &&
                tag !== 'BUTTON' &&
                !target.isContentEditable
            ) {
                e.preventDefault();
            }
        };
        formEl.addEventListener('keydown', handler);
        return () => formEl.removeEventListener('keydown', handler);
    }, [disableSubmitOnEnter]);

    return (
        // Full-width container with MUI Container gutters. The DS Container cannot be
        // used here: its sizing-system wrapper turns maxWidth={false} into max-width: 0%.
        <Box width="100%" px={{ xs: 2, sm: 3 }}>
            {title && (
                <Box mb="1rem" fontWeight="bold">
                    <Typography variant="h5" color="primary">
                        <FormattedMessage
                            id={title.formatId}
                            defaultMessage={title.formatDefault}
                        />
                    </Typography>
                </Box>
            )}
            <FormProvider {...methods}>
                <form
                    ref={formRef}
                    id={formId}
                    style={{
                        ...(isEditNode && { width: '100%' }),
                    }}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    {ControlledFields}
                </form>
            </FormProvider>
        </Box>
    );
};
